import { Bell } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { getNotifications, markAllRead, markRead } from '@/api/notifications'
import NotificationItem from './NotificationItem'

type Props = {
  open: boolean
  onClose: () => void
}

export default function NotificationSheet({ open, onClose }: Props) {
  const qc = useQueryClient()

  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: getNotifications,
    enabled: open,
    refetchInterval: open ? 30000 : false,
  })

  const readMutation = useMutation({
    mutationFn: markRead,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  })

  const readAllMutation = useMutation({
    mutationFn: markAllRead,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  })

  const notifications = data?.results ?? []
  const hasUnread = (data?.unread_count ?? 0) > 0

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full max-w-sm p-0 flex flex-col pt-safe">
        <SheetHeader className="flex flex-row items-center justify-between px-4 py-3 border-b shrink-0">
          <SheetTitle className="text-base">通知</SheetTitle>
          {hasUnread && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-7 mr-8"
              onClick={() => readAllMutation.mutate()}
              disabled={readAllMutation.isPending}
            >
              すべて既読
            </Button>
          )}
        </SheetHeader>

        <div className="flex-1 overflow-y-auto divide-y">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-16 text-muted-foreground">
              <Bell size={32} strokeWidth={1.5} />
              <p className="text-sm">通知はありません</p>
            </div>
          ) : (
            notifications.map((n) => (
              <NotificationItem
                key={n.id}
                notification={n}
                onRead={(id) => readMutation.mutate(id)}
                onClose={onClose}
              />
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
